import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import {
  LayoutGrid,
  List,
  Search,
  X,
  CheckSquare,
  MessageSquare,
  History,
  Trash2,
  Calendar as CalendarIcon,
  Paperclip,
  Upload,
  FileText,
  ChevronLeft,
  ChevronRight,
  Plus,
  Loader2,
  Sparkles,
  AlertTriangle,
  CheckCircle2,
  ShieldAlert,
  Flag,
  Layers
} from 'lucide-react';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { SortButton } from '@/components/ui/SortButton.js';
import { Pagination } from '@/components/ui/Pagination.js';
import { PullToRefresh } from '@/components/ui/PullToRefresh.js';
import { DeleteConfirmModal } from '@/components/ui/DeleteConfirmModal.js';
import { SkeletonKanban, SkeletonTable } from '@/components/ui/skeleton.js';
import { PlatformBadge } from '@/components/ui/PlatformBadge.js';
import { PlatformType } from '@/types/index.js';

interface Task {
  id: string;
  project_id: string;
  milestone_id?: string | null;
  assignee_id?: string | null;
  assignee_name?: string | null;
  title: string;
  description?: string | null;
  status: 'todo' | 'in_progress' | 'in_review' | 'blocked' | 'done';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  progress_percent: number;
  due_date?: string | null;
  project_name: string;
  project_platform?: PlatformType | string | null;
  blocked_reason?: string | null;
  checklist_total?: number;
  checklist_completed?: number;
}

const COLUMNS = [
  { key: 'todo', label: 'To Do', color: 'bg-slate-100 text-slate-700' },
  { key: 'in_progress', label: 'In Progress', color: 'bg-blue-50 text-blue-800' },
  { key: 'in_review', label: 'In Review', color: 'bg-amber-50 text-amber-800' },
  { key: 'blocked', label: 'Blocked', color: 'bg-red-50 text-red-800' },
  { key: 'done', label: 'Done', color: 'bg-emerald-50 text-emerald-800' },
];

export function normalizeTaskStatus(status?: string): string {
  if (!status) return 'todo';
  const s = status.toLowerCase().trim().replace(/[-\s]+/g, '_');
  if (s === 'completed' || s === 'finished') return 'done';
  if (s === 'inprogress' || s === 'in_progress' || s === 'progress') return 'in_progress';
  if (s === 'inreview' || s === 'in_review' || s === 'review') return 'in_review';
  if (s === 'blocked' || s === 'on_hold' || s === 'hold') return 'blocked';
  if (s === 'todo' || s === 'to_do' || s === 'backlog' || s === 'pending') return 'todo';
  return s;
}

export function TasksPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role || 'team_member';
  const isOwner = role === 'owner';
  const isManager = role === 'manager';
  const isViewer = role === 'viewer';

  const canEditTask = (task: any) => {
    if (isViewer) return false;
    if (isOwner || isManager) return true;
    if (task?.assignee_id && user?.id && task.assignee_id === user.id) return true;
    if (task?.assignee_name && user?.name && task.assignee_name.toLowerCase().includes(user.name.toLowerCase())) return true;
    return false;
  };

  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<any[]>([]);
  const [projectFilter, setProjectFilter] = useState('');
  const [milestones, setMilestones] = useState<any[]>([]);
  const [milestoneFilter, setMilestoneFilter] = useState<string>('');
  const [groupByMilestone, setGroupByMilestone] = useState<boolean>(false);
  const [viewMode, setViewMode] = useState<'board' | 'list' | 'calendar'>('board');
  const [mobileStatusFilter, setMobileStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<string>('due_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [newComment, setNewComment] = useState('');
  const [newChecklistText, setNewChecklistText] = useState('');

  // Attachments State
  const [attachments, setAttachments] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // @Mentions State
  const [teamMembers, setTeamMembers] = useState<any[]>([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');

  // Calendar View State
  const [calendarDate, setCalendarDate] = useState(() => new Date(2026, 9, 1)); // Default Oct 2026
  const [selectedDayTasks, setSelectedDayTasks] = useState<{ date: string; tasks: Task[] } | null>(null);
  const [reschedulingTaskId, setReschedulingTaskId] = useState<string | null>(null);
  const [deleteTaskTarget, setDeleteTaskTarget] = useState<{ id: string; title: string } | null>(null);

  // Task Detail Blockers State
  const [taskBlockers, setTaskBlockers] = useState<any[]>([]);
  const [isLoggingBlocker, setIsLoggingBlocker] = useState(false);
  const [blockerForm, setBlockerForm] = useState({
    blocker_description: '',
    ai_diagnosis: '',
    resolution_notes: '',
    severity: 'high' as any,
    status: 'active' as any,
  });

  const [resolvingBlockerId, setResolvingBlockerId] = useState<string | null>(null);
  const [drawerResolveNotes, setDrawerResolveNotes] = useState('');

  const fetchTaskBlockers = async (taskId: string) => {
    try {
      const res = await api.get<{ data: any[] }>(`/api/v1/blockers?task_id=${taskId}`);
      if (res?.data) {
        setTaskBlockers(res.data);
      }
    } catch {
      setTaskBlockers([]);
    }
  };

  const handleResolveTaskBlocker = async (blockerId: string) => {
    try {
      const notes = drawerResolveNotes.trim() || 'Implemented fix following AI recommendations. Issue resolved.';
      await api.patch(`/api/v1/blockers/${blockerId}`, {
        status: 'resolved',
        resolution_notes: notes,
      });
      setResolvingBlockerId(null);
      setDrawerResolveNotes('');
      if (selectedTask?.id) {
        fetchTaskBlockers(selectedTask.id);
        openTaskDetail(selectedTask.id);
      }
      fetchTasks();
    } catch (e: any) {
      alert(e.message || 'Failed to resolve blocker');
    }
  };

  const handleAutoDiagnoseDrawer = () => {
    if (!blockerForm.blocker_description.trim()) {
      alert('Please describe what the blocker was first.');
      return;
    }
    const desc = blockerForm.blocker_description.trim();
    let advice = `AI Diagnosis: Roadblock in "${desc.slice(0, 50)}...". Recommended Solution: Verify dependency declarations, check runtime environment variables, and review recent code commits.`;
    if (/oauth|auth|login|token|stripe|webhook/i.test(desc)) {
      advice = `AI Diagnosis: Authentication or Webhook failure. Check redirect URI whitelist in provider console and verify signing secrets.`;
    } else if (/database|sql|migration|table/i.test(desc)) {
      advice = `AI Diagnosis: Database migration or schema conflict. Ensure column types match model and transaction locks are released.`;
    } else if (/timeout|memory|heap|slow/i.test(desc)) {
      advice = `AI Diagnosis: Memory leak or execution timeout. Chunk processing and stream large datasets instead of in-memory load.`;
    }
    setBlockerForm((prev) => ({
      ...prev,
      ai_diagnosis: prev.ai_diagnosis ? `${prev.ai_diagnosis}\n${advice}` : advice,
    }));
  };

  const handleLogTaskBlocker = async () => {
    if (!blockerForm.blocker_description.trim() || !selectedTask) return;
    try {
      await api.post('/api/v1/blockers', {
        task_id: selectedTask.id,
        project_id: selectedTask.project_id,
        blocker_description: blockerForm.blocker_description.trim(),
        ai_diagnosis: blockerForm.ai_diagnosis.trim(),
        resolution_notes: blockerForm.resolution_notes.trim(),
        severity: blockerForm.severity,
        status: blockerForm.status,
      });
      setIsLoggingBlocker(false);
      setBlockerForm({
        blocker_description: '',
        ai_diagnosis: '',
        resolution_notes: '',
        severity: 'high',
        status: 'active',
      });
      openTaskDetail(selectedTask.id);
      fetchTasks();
    } catch (e: any) {
      alert(e.message || 'Failed to log blocker');
    }
  };

  const handleUpdateTaskDueDate = async (taskId: string, newDueDate: string) => {
    if (!isOwner && !isManager) return;
    try {
      await api.patch(`/api/v1/tasks/${taskId}`, { due_date: newDueDate });
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, due_date: newDueDate } : t))
      );
      if (selectedTask?.id === taskId) {
        setSelectedTask((prev: any) => ({ ...prev, due_date: newDueDate }));
      }
      fetchTasks();
    } catch (e: any) {
      alert(e.message || 'Failed to reschedule task');
    }
  };

  const fetchTasks = async () => {
    try {
      const res = await api.get<{ data: Task[] }>('/api/v1/tasks');
      if (res?.data) {
        setTasks(res.data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await api.get<{ data: any[] }>('/api/v1/projects');
      const list = res?.data || (Array.isArray(res) ? res : []);
      setProjects(list);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchMilestones = async () => {
    try {
      const res = await api.get<{ data: any[] }>('/api/v1/milestones');
      const list = res?.data || (Array.isArray(res) ? res : []);
      setMilestones(list);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const res = await api.get<{ data: any[] }>('/api/v1/team');
      setTeamMembers(res?.data || []);
    } catch {}
  };

  const fetchAttachments = async (taskId: string) => {
    try {
      const res = await api.get<{ data: any[] }>(`/api/v1/tasks/${taskId}/attachments`);
      setAttachments(res?.data || []);
    } catch {
      setAttachments([]);
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchProjects();
    fetchMilestones();
    fetchTeamMembers();
  }, []);

  useEffect(() => {
    if (id) {
      openTaskDetail(id);
    }
  }, [id]);

  const handleUploadAttachment = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedTask) return;
    setAttachmentError(null);

    // 10 MB per-file size limit validation
    if (file.size > 10 * 1024 * 1024) {
      setAttachmentError('Attachment exceeds the 10 MB size limit.');
      return;
    }

    setIsUploading(true);
    try {
      const res = await api.post(`/api/v1/tasks/${selectedTask.id}/attachments`, {
        name: file.name,
        size: file.size,
        mime_type: file.type || 'application/octet-stream',
        url: '#'
      });
      if (res?.data) {
        setAttachments((prev) => [...prev, res.data]);
      }
    } catch (err: any) {
      setAttachmentError(err.message || 'Failed to upload attachment');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAttachment = async (attId: string) => {
    if (!selectedTask) return;
    try {
      await api.delete(`/api/v1/tasks/${selectedTask.id}/attachments/${attId}`);
      setAttachments((prev) => prev.filter((a) => a.id !== attId));
    } catch (err: any) {
      alert(err.message || 'Failed to delete attachment');
    }
  };

  const handleCommentChange = (val: string) => {
    setNewComment(val);
    const match = val.match(/@([a-zA-Z0-9_]*)$/);
    if (match) {
      setShowMentions(true);
      setMentionFilter(match[1].toLowerCase());
    } else {
      setShowMentions(false);
    }
  };

  const handleSelectMention = (memberName: string) => {
    const updated = newComment.replace(/@([a-zA-Z0-9_]*)$/, `@${memberName} `);
    setNewComment(updated);
    setShowMentions(false);
  };

  const handleReassignProject = async (newProjectId: string) => {
    if (!selectedTask || !newProjectId) return;
    const targetProj = projects.find((p) => p.id === newProjectId);
    const newProjectName = targetProj?.name || 'General Project';

    try {
      const willUnlinkMilestone = Boolean(selectedTask.milestone_id);
      await api.patch(`/api/v1/tasks/${selectedTask.id}`, {
        project_id: newProjectId,
        project_name: newProjectName,
        ...(willUnlinkMilestone ? { milestone_id: null } : {}),
      });
      setSelectedTask((prev: any) => ({
        ...prev,
        project_id: newProjectId,
        project_name: newProjectName,
        milestone_id: willUnlinkMilestone ? null : prev.milestone_id,
      }));
      setTasks((prev) =>
        prev.map((t) =>
          t.id === selectedTask.id
            ? { ...t, project_id: newProjectId, project_name: newProjectName, milestone_id: willUnlinkMilestone ? null : t.milestone_id }
            : t
        )
      );
      fetchTasks();
    } catch (e: any) {
      alert(e.message || 'Failed to reassign task to project');
    }
  };

  const handleUpdateDueDate = async (dueDate: string | null) => {
    if (!selectedTask) return;
    try {
      await api.patch(`/api/v1/tasks/${selectedTask.id}`, {
        due_date: dueDate || null,
      });
      setSelectedTask((prev: any) => ({
        ...prev,
        due_date: dueDate || null,
      }));
      setTasks((prev) =>
        prev.map((t) =>
          t.id === selectedTask.id ? { ...t, due_date: dueDate || null } : t
        )
      );
      fetchTasks();
    } catch (e: any) {
      alert(e.message || 'Failed to update due date');
    }
  };

  const handleUpdateTaskMilestone = async (milestoneId: string | null) => {
    if (!selectedTask) return;
    try {
      const val = milestoneId ? milestoneId : null;
      await api.patch(`/api/v1/tasks/${selectedTask.id}`, {
        milestone_id: val,
      });
      setSelectedTask((prev: any) => ({
        ...prev,
        milestone_id: val,
      }));
      setTasks((prev) =>
        prev.map((t) =>
          t.id === selectedTask.id ? { ...t, milestone_id: val } : t
        )
      );
      fetchTasks();
    } catch (e: any) {
      alert(e.message || 'Failed to update milestone');
    }
  };

  const openTaskDetail = async (taskId: string) => {
    try {
      const res = await api.get<{ data: any }>(`/api/v1/tasks/${taskId}`);
      if (res?.data) {
        setSelectedTask(res.data);
        fetchAttachments(taskId);
        fetchTaskBlockers(taskId);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCloseDetail = () => {
    setSelectedTask(null);
    setAttachments([]);
    setAttachmentError(null);
    setTaskBlockers([]);
    setIsLoggingBlocker(false);
    if (id) {
      navigate('/tasks', { replace: true });
    }
  };

  const handleDeleteTask = (taskId: string, title?: string) => {
    setDeleteTaskTarget({ id: taskId, title: title || 'this task' });
  };

  const confirmDeleteTask = async () => {
    if (!deleteTaskTarget) return;
    const { id: taskId } = deleteTaskTarget;
    try {
      await api.delete(`/api/v1/tasks/${taskId}`);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      if (selectedTask?.id === taskId) {
        handleCloseDetail();
      }
      setDeleteTaskTarget(null);
      fetchTasks();
    } catch (e: any) {
      alert(e.message || 'Failed to delete task');
    }
  };

  const handleUpdateStatus = async (status: string) => {
    if (!selectedTask) return;
    let extraBody: any = {};
    if (status === 'blocked') {
      const reason = window.prompt ? window.prompt('Please enter the blocked reason:') : 'Blocked on dependencies';
      extraBody.blocked_reason = reason || 'Blocked on review or dependencies';
    } else if (selectedTask.status === 'done' && status !== 'done') {
      const reason = window.prompt ? window.prompt('Please explain why reopening this task:') : 'Reopened for revisions';
      extraBody.reopen_reason = reason || 'Reopened for revisions';
    }

    try {
      await api.patch(`/api/v1/tasks/${selectedTask.id}`, { status, ...extraBody });
      openTaskDetail(selectedTask.id);
      fetchTasks();
    } catch (e: any) {
      alert(e.message || 'Status transition denied');
    }
  };

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedTask) return;
    try {
      await api.post(`/api/v1/tasks/${selectedTask.id}/comments`, { content: newComment });
      setNewComment('');
      setShowMentions(false);
      openTaskDetail(selectedTask.id);
    } catch (e) {
      console.error(e);
    }
  };

  const handleAddChecklist = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newChecklistText.trim() || !selectedTask) return;
    try {
      await api.post(`/api/v1/tasks/${selectedTask.id}/checklist`, { text: newChecklistText });
      setNewChecklistText('');
      openTaskDetail(selectedTask.id);
    } catch (e) {
      console.error(e);
    }
  };

  const handleToggleChecklist = async (itemId: string, current: boolean) => {
    try {
      await api.patch(`/api/v1/tasks/${selectedTask.id}/checklist/${itemId}`, {
        is_completed: !current,
      });
      openTaskDetail(selectedTask.id);
    } catch (e) {
      console.error(e);
    }
  };

  const filteredTasks = tasks.filter((t) => {
    const matchesSearch = search ? t.title.toLowerCase().includes(search.toLowerCase()) : true;
    const selectedPrj = projectFilter ? projects.find((p) => p.id === projectFilter || p.slug === projectFilter) : null;
    const matchesProject = projectFilter
      ? (t.project_id === projectFilter || (selectedPrj?.slug && t.project_id === selectedPrj.slug) || (selectedPrj?.id && t.project_id === selectedPrj.id) || t.project_name?.toLowerCase() === projectFilter.toLowerCase())
      : true;
    const matchesMilestone = milestoneFilter
      ? (milestoneFilter === 'unlinked' ? !t.milestone_id : t.milestone_id === milestoneFilter || String(t.milestone_id) === String(milestoneFilter))
      : true;
    return matchesSearch && matchesProject && matchesMilestone;
  });

  const milestoneGroups = useMemo(() => {
    if (!groupByMilestone) return [];
    const groups: { milestone: any | null; tasks: Task[] }[] = [];
    const selP = projectFilter ? projects.find((p) => p.id === projectFilter || p.slug === projectFilter) : null;
    const relevantMilestones = projectFilter
      ? milestones.filter((m) => m.project_id === projectFilter || (selP?.slug && m.project_id === selP.slug) || (selP?.id && m.project_id === selP.id))
      : milestones;

    for (const m of relevantMilestones) {
      const mTasks = filteredTasks.filter((t) => t.milestone_id === m.id || String(t.milestone_id) === String(m.id));
      if (mTasks.length > 0 || projectFilter) {
        groups.push({ milestone: m, tasks: mTasks });
      }
    }
    const unlinked = filteredTasks.filter((t) => !t.milestone_id);
    if (unlinked.length > 0 || groups.length === 0) {
      groups.push({ milestone: null, tasks: unlinked });
    }
    return groups;
  }, [groupByMilestone, filteredTasks, milestones, projectFilter, projects]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedTasks = [...filteredTasks].sort((a: any, b: any) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    if (sortField === 'due_date') {
      const aTime = aVal ? new Date(aVal).getTime() : Infinity;
      const bTime = bVal ? new Date(bVal).getTime() : Infinity;
      return sortDir === 'asc' ? aTime - bTime : bTime - aTime;
    }
    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(sortedTasks.length / pageSize));
  const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (validCurrentPage - 1) * pageSize;
  const paginatedTasks = sortedTasks.slice(startIndex, startIndex + pageSize);

  // Calendar Math
  const calYear = calendarDate.getFullYear();
  const calMonth = calendarDate.getMonth();
  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDayOfWeek = (new Date(calYear, calMonth, 1).getDay() + 6) % 7; // Monday = 0
  const monthName = calendarDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <PullToRefresh onRefresh={fetchTasks}>
      <div className="space-y-6">
        {/* Header Controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-title">Tasks &amp; Deliverables</h1>
            <p className="helper-text mt-1">
              Cross-project task management, status transitions, and audit records.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {user?.role !== 'viewer' && (
              <Button
                onClick={() => window.dispatchEvent(new CustomEvent('tsos_open_quick_add'))}
                className="bg-brand-teal text-white hover:bg-teal-700 font-semibold min-h-[44px] text-xs px-3.5 flex items-center space-x-1.5 shadow-sm"
              >
                <Plus className="h-4 w-4" />
                <span>New Task</span>
              </Button>
            )}
          </div>
        </div>

        {/* Filters and View Switcher */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-brand-surface dark:bg-[#12211F] p-3 rounded-lg border border-brand-border">
          <div className="flex flex-1 flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
              <Input
                type="search"
                placeholder="Search tasks..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10 text-base md:text-xs"
              />
            </div>
            <select
              value={projectFilter}
              onChange={(e) => {
                const newProj = e.target.value;
                setProjectFilter(newProj);
                if (newProj && milestoneFilter && milestoneFilter !== 'unlinked') {
                  const m = milestones.find((x) => x.id === milestoneFilter);
                  if (m && m.project_id !== newProj) {
                    setMilestoneFilter('');
                  }
                }
              }}
              className="h-10 rounded-md border border-brand-border bg-brand-card dark:bg-[#152724] px-3 text-base md:text-xs text-brand-ink focus:outline-none focus:ring-1 focus:ring-brand min-h-[44px]"
            >
              <option value="">All Projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <select
              value={milestoneFilter}
              onChange={(e) => setMilestoneFilter(e.target.value)}
              className="h-10 rounded-md border border-brand-border bg-brand-card dark:bg-[#152724] px-3 text-base md:text-xs text-brand-ink focus:outline-none focus:ring-1 focus:ring-brand min-h-[44px]"
            >
              <option value="">All Milestones</option>
              <option value="unlinked">No milestone (Unlinked)</option>
              {(projectFilter
                ? milestones.filter((m) => {
                    const selP = projects.find((p) => p.id === projectFilter || p.slug === projectFilter);
                    return m.project_id === projectFilter || (selP?.slug && m.project_id === selP.slug) || (selP?.id && m.project_id === selP.id);
                  })
                : milestones
              ).map((m) => (
                <option key={m.id} value={m.id}>
                  {projectFilter ? m.title : `${m.title} (${projects.find((p) => p.id === m.project_id || p.slug === m.project_id)?.name || 'Project'})`}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center space-x-1.5 self-end md:self-auto">
            <Button
              size="sm"
              variant={groupByMilestone ? 'secondary' : 'ghost'}
              onClick={() => setGroupByMilestone((prev) => !prev)}
              className={`min-h-[40px] px-2.5 text-xs font-semibold ${groupByMilestone ? 'bg-brand-teal/20 text-brand-teal border border-brand-teal/30' : 'text-brand-muted'}`}
              title="Group tasks by milestone"
            >
              <Layers className="h-4 w-4 mr-1.5" />
              <span>Group by Milestone</span>
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'board' ? 'secondary' : 'ghost'}
              onClick={() => setViewMode('board')}
              className={`min-h-[40px] px-2.5 text-xs font-semibold ${viewMode === 'board' ? 'bg-brand-tint text-brand' : 'text-brand-muted'}`}
              title="Kanban Board view"
            >
              <LayoutGrid className="h-4 w-4 mr-1.5" />
              <span>Board</span>
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              onClick={() => setViewMode('list')}
              className={`min-h-[40px] px-2.5 text-xs font-semibold ${viewMode === 'list' ? 'bg-brand-tint text-brand' : 'text-brand-muted'}`}
              title="Table List view"
            >
              <List className="h-4 w-4 mr-1.5" />
              <span>List</span>
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
              onClick={() => setViewMode('calendar')}
              className={`min-h-[40px] px-2.5 text-xs font-semibold ${viewMode === 'calendar' ? 'bg-brand-tint text-brand' : 'text-brand-muted'}`}
              title="Calendar Schedule view"
            >
              <CalendarIcon className="h-4 w-4 mr-1.5" />
              <span>Calendar</span>
            </Button>
          </div>
        </div>

        {loading ? (
          viewMode === 'list' ? (
            <SkeletonTable rows={8} cols={5} />
          ) : (
            <SkeletonKanban columns={5} />
          )
        ) : (
          <>
            {/* View Mode 1: Kanban Board */}
            {viewMode === 'board' && (
          <div className="space-y-4">
            {/* Mobile Column Status Switcher Pills */}
            <div className="flex md:hidden items-center space-x-1.5 overflow-x-auto pb-2 -mx-1 px-1">
              <button
                onClick={() => setMobileStatusFilter('all')}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap min-h-[36px] transition-colors ${
                  mobileStatusFilter === 'all'
                    ? 'bg-brand text-white'
                    : 'bg-slate-100 dark:bg-slate-800 text-brand-muted'
                }`}
              >
                All ({filteredTasks.length})
              </button>
              {COLUMNS.map((col) => {
                const count = filteredTasks.filter((t) => normalizeTaskStatus(t.status) === col.key).length;
                return (
                  <button
                    key={col.key}
                    onClick={() => setMobileStatusFilter(col.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap min-h-[36px] transition-colors ${
                      mobileStatusFilter === col.key
                        ? 'bg-brand text-white'
                        : 'bg-slate-100 dark:bg-slate-800 text-brand-muted'
                    }`}
                  >
                    {col.label} ({count})
                  </button>
                );
              })}
            </div>

            {/* Kanban Columns Grid / Swipeable Container */}
            {groupByMilestone ? (
              <div className="space-y-6">
                {milestoneGroups.map((group) => (
                  <div key={group.milestone?.id || 'unlinked'} className="space-y-3 bg-brand-surface/40 dark:bg-[#101E1C]/60 p-3.5 rounded-xl border border-brand-border">
                    <div className="flex items-center justify-between pb-2 border-b border-brand-border">
                      <div className="flex items-center space-x-2">
                        <Flag className="w-4 h-4 text-brand-teal shrink-0" />
                        <h3 className="text-xs font-bold text-brand-navy dark:text-brand-ink">
                          {group.milestone ? group.milestone.title : 'Unlinked Tasks (No Milestone)'}
                        </h3>
                        {group.milestone && (
                          <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-brand-teal/10 text-brand-teal border border-brand-teal/20">
                            {group.milestone.status}
                          </span>
                        )}
                      </div>
                      <span className="text-xs font-mono text-brand-muted">
                        {group.tasks.length} task{group.tasks.length === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="flex md:grid md:grid-cols-5 gap-3 overflow-x-auto pb-2">
                      {COLUMNS.map((col) => {
                        const colTasks = group.tasks.filter((t) => normalizeTaskStatus(t.status) === col.key);
                        return (
                          <div
                            key={col.key}
                            className="bg-brand-surface dark:bg-[#12211F] rounded-xl p-2.5 flex flex-col w-[260px] md:w-auto shrink-0 md:shrink border border-brand-border"
                          >
                            <div className="flex items-center justify-between pb-1.5 mb-1.5 border-b border-brand-border">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-brand-ink">{col.label}</span>
                              <span className="text-[10px] font-mono text-brand-muted px-1.5 py-0.5 rounded bg-brand-surface dark:bg-[#152724] border border-brand-border">{colTasks.length}</span>
                            </div>
                            <div className="space-y-2 flex-1">
                              {colTasks.length === 0 ? (
                                <div className="py-4 text-center text-[10px] text-brand-muted border border-dashed border-brand-border/60 rounded">No tasks</div>
                              ) : (
                                colTasks.map((task) => (
                                  <Card
                                    key={task.id}
                                    onClick={() => openTaskDetail(task.id)}
                                    className="cursor-pointer hover:border-brand-teal transition-all shadow-none hover:shadow-sm bg-white dark:bg-[#162A27] border-brand-border"
                                  >
                                    <CardContent className="p-3 space-y-2">
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-1.5 truncate max-w-[150px]">
                                          <PlatformBadge platform={task.project_platform || 'direct'} size="xs" />
                                          <span className="text-[10px] font-semibold text-brand uppercase truncate">
                                            {task.project_name}
                                          </span>
                                        </div>
                                        <div className="flex items-center space-x-1">
                                          <Badge variant={task.priority as any} className="text-[9px] uppercase px-1 py-0">
                                            {task.priority}
                                          </Badge>
                                          {isOwner && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteTask(task.id, task.title);
                                              }}
                                              className="text-brand-muted hover:text-rose-600 p-1 rounded transition-colors min-h-[32px] min-w-[32px] flex items-center justify-center"
                                              title="Delete task"
                                              aria-label="Delete task"
                                            >
                                              <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                      <h4 className="text-xs font-semibold text-brand-ink line-clamp-2">
                                        {task.title}
                                      </h4>
                                      {(() => {
                                        const tm = task.milestone_id ? milestones.find((m) => m.id === task.milestone_id || String(m.id) === String(task.milestone_id)) : null;
                                        if (!tm) return null;
                                        return (
                                          <div className="flex items-center space-x-1 text-[10px] text-brand-teal bg-brand-teal/10 px-1.5 py-0.5 rounded border border-brand-teal/20 w-fit max-w-full truncate" title={`Milestone: ${tm.title}`}>
                                            <Flag className="h-2.5 w-2.5 shrink-0" />
                                            <span className="truncate">{tm.title}</span>
                                          </div>
                                        );
                                      })()}
                                      {task.due_date && (
                                        <div className="flex items-center text-[10px] text-brand-muted space-x-1 pt-0.5">
                                          <CalendarIcon className="h-3 w-3 text-brand" />
                                          <span>{new Date(task.due_date).toLocaleDateString()}</span>
                                        </div>
                                      )}
                                      <div className="flex items-center justify-between pt-1 text-[11px] text-brand-muted">
                                        <span className="font-mono">{task.progress_percent}%</span>
                                        {task.assignee_name && (
                                          <span className="truncate max-w-[100px] font-medium text-brand-ink">
                                            {task.assignee_name}
                                          </span>
                                        )}
                                      </div>
                                    </CardContent>
                                  </Card>
                                ))
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex md:grid md:grid-cols-5 gap-4 overflow-x-auto snap-x snap-mandatory pb-4">
                {COLUMNS.filter((col) => mobileStatusFilter === 'all' || mobileStatusFilter === col.key).map((col) => {
                  const colTasks = filteredTasks.filter((t) => normalizeTaskStatus(t.status) === col.key);
                  return (
                    <div
                      key={col.key}
                      className="bg-brand-surface dark:bg-[#12211F] rounded-xl p-3 flex flex-col w-[280px] sm:w-[320px] md:w-auto shrink-0 md:shrink snap-start border border-brand-border"
                    >
                      {/* Column Header */}
                      <div className="flex items-center justify-between pb-2 mb-2 border-b border-brand-border">
                        <span className="text-xs font-bold uppercase tracking-wider text-brand-ink">
                          {col.label}
                        </span>
                        <span className="text-xs font-mono font-semibold text-brand-muted bg-brand-surface dark:bg-[#152724] px-2 py-0.5 rounded-full border border-brand-border">
                          {colTasks.length}
                        </span>
                      </div>

                      {/* Task Cards */}
                      <div className="space-y-2 flex-1">
                        {colTasks.length === 0 ? (
                          <div className="py-8 text-center text-xs text-brand-muted border-2 border-dashed border-brand-border rounded-lg">
                            No tasks in this lane
                          </div>
                        ) : (
                          colTasks.map((task) => (
                            <Card
                              key={task.id}
                              onClick={() => openTaskDetail(task.id)}
                              className="cursor-pointer hover:border-brand-teal transition-all shadow-none hover:shadow-sm bg-white dark:bg-[#162A27] border-brand-border"
                            >
                              <CardContent className="p-3 space-y-2">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-1.5 truncate max-w-[150px]">
                                    <PlatformBadge platform={task.project_platform || 'direct'} size="xs" />
                                    <span className="text-[10px] font-semibold text-brand uppercase truncate">
                                      {task.project_name}
                                    </span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <Badge variant={task.priority as any} className="text-[9px] uppercase px-1 py-0">
                                      {task.priority}
                                    </Badge>
                                    {isOwner && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteTask(task.id, task.title);
                                        }}
                                        className="text-brand-muted hover:text-rose-600 p-1 rounded transition-colors min-h-[32px] min-w-[32px] flex items-center justify-center"
                                        title="Delete task"
                                        aria-label="Delete task"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <h4 className="text-xs font-semibold text-brand-ink line-clamp-2">
                                  {task.title}
                                </h4>
                                {(() => {
                                  const tm = task.milestone_id ? milestones.find((m) => m.id === task.milestone_id || String(m.id) === String(task.milestone_id)) : null;
                                  if (!tm) return null;
                                  return (
                                    <div className="flex items-center space-x-1 text-[10px] text-brand-teal bg-brand-teal/10 px-1.5 py-0.5 rounded border border-brand-teal/20 w-fit max-w-full truncate" title={`Milestone: ${tm.title}`}>
                                      <Flag className="h-2.5 w-2.5 shrink-0" />
                                      <span className="truncate">{tm.title}</span>
                                    </div>
                                  );
                                })()}
                                {task.due_date && (
                                  <div className="flex items-center text-[10px] text-brand-muted space-x-1 pt-0.5">
                                    <CalendarIcon className="h-3 w-3 text-brand" />
                                    <span>{new Date(task.due_date).toLocaleDateString()}</span>
                                  </div>
                                )}
                                <div className="flex items-center justify-between pt-1 text-[11px] text-brand-muted">
                                  <span className="font-mono">{task.progress_percent}%</span>
                                  {task.assignee_name && (
                                    <span className="truncate max-w-[100px] font-medium text-brand-ink">
                                      {task.assignee_name}
                                    </span>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* View Mode 2: Table List & Stacked Mobile Cards */}
        {viewMode === 'list' && (
          <div className="space-y-3">
            {/* Desktop Table View */}
            <div className="hidden md:block bg-white dark:bg-[#12211F] rounded-xl border border-brand-border overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-brand-border bg-brand-surface dark:bg-[#0E1A18]">
                      <th className="py-2.5 px-4">
                        <SortButton label="Project" field="project_name" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                      </th>
                      <th className="py-2.5 px-4">
                        <SortButton label="Task Title" field="title" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                      </th>
                      <th className="py-2.5 px-4">
                        <SortButton label="Milestone" field="milestone_id" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                      </th>
                      <th className="py-2.5 px-4">
                        <SortButton label="Status" field="status" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                      </th>
                      <th className="py-2.5 px-4">
                        <SortButton label="Priority" field="priority" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                      </th>
                      <th className="py-2.5 px-4">
                        <SortButton label="Progress" field="progress_percent" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                      </th>
                      <th className="py-2.5 px-4">
                        <SortButton label="Assignee" field="assignee_name" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                      </th>
                      <th className="py-2.5 px-4">
                        <SortButton label="Due Date" field="due_date" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
                      </th>
                      {isOwner && <th className="py-2.5 px-4 text-right text-xs font-semibold text-brand-muted">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border text-xs">
                    {paginatedTasks.length === 0 ? (
                      <tr>
                        <td colSpan={isOwner ? 9 : 8} className="py-8 text-center text-brand-muted">
                          No tasks found matching your filter criteria.
                        </td>
                      </tr>
                    ) : (
                      paginatedTasks.map((task) => (
                        <tr
                          key={task.id}
                          onClick={() => openTaskDetail(task.id)}
                          className="hover:bg-brand-surface dark:hover:bg-[#162A27] cursor-pointer transition-colors"
                        >
                          <td className="py-3 px-4 font-semibold text-brand text-[11px]">
                            <div className="flex items-center space-x-1.5">
                              <PlatformBadge platform={task.project_platform || 'direct'} size="xs" />
                              <span className="uppercase">{task.project_name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 font-medium text-brand-ink">
                            {task.title}
                          </td>
                          <td className="py-3 px-4">
                            {(() => {
                              const tm = task.milestone_id ? milestones.find((m) => m.id === task.milestone_id || String(m.id) === String(task.milestone_id)) : null;
                              return tm ? (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-brand-teal/10 text-brand-teal border border-brand-teal/20" title={tm.title}>
                                  <Flag className="w-2.5 h-2.5 shrink-0" />
                                  <span className="truncate max-w-[120px]">{tm.title}</span>
                                </span>
                              ) : (
                                <span className="text-[11px] text-brand-muted italic">No milestone</span>
                              );
                            })()}
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant={task.status as any} className="text-[10px] capitalize">
                              {task.status.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant={task.priority as any} className="text-[10px] uppercase">
                              {task.priority}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 font-mono font-bold text-brand-ink">
                            {task.progress_percent}%
                          </td>
                          <td className="py-3 px-4 text-brand-muted">
                            {task.assignee_name || 'Unassigned'}
                          </td>
                          <td className="py-3 px-4 text-brand-muted whitespace-nowrap">
                            {task.due_date ? new Date(task.due_date).toLocaleDateString() : <span className="text-brand-muted">No due date</span>}
                          </td>
                          {isOwner && (
                            <td className="py-3 px-4 text-right">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteTask(task.id, task.title);
                                }}
                                className="text-brand-muted hover:text-rose-600 p-1.5 rounded transition-colors"
                                title="Delete task"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobile Stacked Card View (360px safe, 0 horizontal scroll) */}
            <div className="md:hidden space-y-3">
              {paginatedTasks.length === 0 ? (
                <div className="p-8 text-center text-xs text-brand-muted bg-brand-surface dark:bg-[#12211F] rounded-xl border border-brand-border">
                  No tasks found matching your filter criteria.
                </div>
              ) : (
                paginatedTasks.map((task) => (
                  <div
                    key={task.id}
                    onClick={() => openTaskDetail(task.id)}
                    className="p-3.5 rounded-xl bg-brand-surface dark:bg-[#12211F] border border-brand-border space-y-2 cursor-pointer shadow-sm active:scale-[0.99] transition-transform"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1.5 truncate max-w-[180px]">
                        <PlatformBadge platform={task.project_platform || 'direct'} size="xs" />
                        <span className="text-[10px] font-bold text-brand uppercase truncate">
                          {task.project_name}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1.5">
                        <Badge variant={task.status as any} className="text-[9px] capitalize px-1.5 py-0">
                          {task.status.replace('_', ' ')}
                        </Badge>
                        <Badge variant={task.priority as any} className="text-[9px] uppercase px-1.5 py-0">
                          {task.priority}
                        </Badge>
                        {isOwner && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteTask(task.id, task.title);
                            }}
                            className="text-brand-muted hover:text-rose-600 p-1 rounded transition-colors ml-1"
                            title="Delete task"
                            aria-label="Delete task"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    <h4 className="text-xs font-bold text-brand-ink leading-snug">
                      {task.title}
                    </h4>
                    {(() => {
                      const tm = task.milestone_id ? milestones.find((m) => m.id === task.milestone_id || String(m.id) === String(task.milestone_id)) : null;
                      if (!tm) return null;
                      return (
                        <div className="flex items-center space-x-1 text-[10px] text-brand-teal bg-brand-teal/10 px-1.5 py-0.5 rounded border border-brand-teal/20 w-fit">
                          <Flag className="h-2.5 w-2.5 shrink-0" />
                          <span className="truncate">{tm.title}</span>
                        </div>
                      );
                    })()}
                    <div className="flex items-center justify-between pt-1 text-[11px] text-brand-muted border-t border-brand-border/60">
                      <span>{task.assignee_name || 'Unassigned'}</span>
                      <div className="flex items-center space-x-2">
                        {task.due_date && <span>Due {new Date(task.due_date).toLocaleDateString()}</span>}
                        <span className="font-mono font-bold text-brand-ink">{task.progress_percent}%</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            <Pagination
              totalItems={sortedTasks.length}
              currentPage={validCurrentPage}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
            />
          </div>
        )}

        {/* View Mode 3: Calendar Schedule View */}
        {viewMode === 'calendar' && (
          <div className="bg-brand-surface dark:bg-[#12211F] rounded-xl border border-brand-border p-4 space-y-4 shadow-sm">
            {/* Calendar Controls */}
            <div className="flex items-center justify-between pb-2 border-b border-brand-border">
              <div className="flex items-center space-x-2">
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => setCalendarDate(new Date(calYear, calMonth - 1, 1))}
                  className="h-8 w-8 min-h-[36px] min-w-[36px]"
                  title="Previous month"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h3 className="text-sm font-bold text-brand-ink min-w-[140px] text-center">
                  {monthName}
                </h3>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => setCalendarDate(new Date(calYear, calMonth + 1, 1))}
                  className="h-8 w-8 min-h-[36px] min-w-[36px]"
                  title="Next month"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              <span className="text-xs text-brand-muted">
                {filteredTasks.filter((t) => t.due_date && t.due_date.startsWith(`${calYear}-${String(calMonth + 1).padStart(2, '0')}`)).length} due deliverables this month
              </span>
            </div>

            {/* Calendar Days Header */}
            <div className="grid grid-cols-7 gap-1 text-center font-bold text-[11px] text-brand-muted uppercase tracking-wider py-1">
              <div>Mon</div>
              <div>Tue</div>
              <div>Wed</div>
              <div>Thu</div>
              <div>Fri</div>
              <div>Sat</div>
              <div>Sun</div>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1.5 sm:gap-2">
              {/* Empty leading padding days */}
              {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                <div key={`empty-${i}`} className="min-h-[70px] sm:min-h-[90px] rounded-lg bg-slate-50/40 dark:bg-slate-900/20" />
              ))}

              {/* Days of Month */}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const dayNum = i + 1;
                const dateKey = `${calYear}-${String(calMonth + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
                const dayTasks = filteredTasks.filter((t) => t.due_date === dateKey);
                const isToday = new Date().toISOString().slice(0, 10) === dateKey;

                return (
                  <div
                    key={dateKey}
                    onClick={() => {
                      setSelectedDayTasks({ date: dateKey, tasks: dayTasks });
                    }}
                    onDragOver={(e) => {
                      if (isOwner || isManager) {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = 'move';
                      }
                    }}
                    onDrop={async (e) => {
                      if (!isOwner && !isManager) return;
                      e.preventDefault();
                      const taskId = e.dataTransfer.getData('text/plain') || reschedulingTaskId;
                      if (taskId) {
                        await handleUpdateTaskDueDate(taskId, dateKey);
                        setReschedulingTaskId(null);
                      }
                    }}
                    className={`min-h-[75px] sm:min-h-[95px] p-1.5 rounded-lg border transition-all flex flex-col justify-between cursor-pointer hover:border-brand/70 hover:shadow-sm ${
                      isToday
                        ? 'border-brand bg-brand-tint/20 dark:bg-emerald-950/20'
                        : 'border-brand-border bg-white dark:bg-[#162A27]'
                    }`}
                    title={`Click to view tasks for ${dateKey} ${isOwner || isManager ? '(Drop task here to reschedule)' : ''}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-[11px] font-bold ${isToday ? 'text-brand font-extrabold' : 'text-brand-ink'}`}>
                        {dayNum}
                      </span>
                      {dayTasks.length > 0 && (
                        <span className="text-[9px] font-mono font-bold bg-brand-tint text-brand px-1 rounded-full">
                          {dayTasks.length}
                        </span>
                      )}
                    </div>

                    {/* Task Pills on Day */}
                    <div className="space-y-1 mt-1 flex-1 overflow-y-auto max-h-[50px] sm:max-h-[65px]">
                      {dayTasks.map((t) => (
                        <div
                          key={t.id}
                          draggable={isOwner || isManager}
                          onDragStart={(e) => {
                            e.stopPropagation();
                            e.dataTransfer.setData('text/plain', t.id);
                            setReschedulingTaskId(t.id);
                          }}
                          onDragEnd={() => setReschedulingTaskId(null)}
                          onClick={(e) => {
                            e.stopPropagation();
                            openTaskDetail(t.id);
                          }}
                          className={`text-[10px] truncate p-1 rounded transition-colors font-medium cursor-pointer ${
                            reschedulingTaskId === t.id
                              ? 'opacity-50 ring-2 ring-brand'
                              : 'bg-slate-100 dark:bg-slate-800 text-brand-ink hover:bg-brand hover:text-white'
                          }`}
                          title={`${t.project_name}: ${t.title} ${isOwner || isManager ? '— Drag to another day to reschedule' : ''}`}
                        >
                          {t.title}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Click Day Task Inspection Modal */}
            {selectedDayTasks && (
              <div
                className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
                onClick={() => setSelectedDayTasks(null)}
              >
                <div
                  className="bg-white dark:bg-[#12211F] rounded-xl border border-brand-border shadow-2xl max-w-lg w-full p-5 space-y-4 animate-in zoom-in-95 duration-150"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between border-b border-brand-border pb-3">
                    <div className="flex items-center space-x-2">
                      <CalendarIcon className="h-4 w-4 text-brand" />
                      <h4 className="text-sm font-bold text-brand-ink">
                        Tasks Due on {new Date(selectedDayTasks.date + 'T12:00:00Z').toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                      </h4>
                    </div>
                    <button
                      onClick={() => setSelectedDayTasks(null)}
                      className="p-1 rounded-lg text-brand-muted hover:text-brand-ink min-h-[36px] min-w-[36px] flex items-center justify-center"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {selectedDayTasks.tasks.length === 0 ? (
                      <div className="py-8 text-center text-xs text-brand-muted">
                        No tasks scheduled for this day.
                      </div>
                    ) : (
                      selectedDayTasks.tasks.map((t) => (
                        <div
                          key={t.id}
                          onClick={() => {
                            setSelectedDayTasks(null);
                            openTaskDetail(t.id);
                          }}
                          className="p-3 rounded-lg border border-brand-border bg-brand-surface dark:bg-[#152724] hover:border-brand cursor-pointer transition-colors space-y-1.5"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold text-brand uppercase truncate max-w-[200px]">
                              {t.project_name}
                            </span>
                            <div className="flex items-center space-x-1.5">
                              <Badge variant={t.status as any} className="text-[9px] capitalize px-1.5 py-0">
                                {t.status.replace('_', ' ')}
                              </Badge>
                              <Badge variant={t.priority as any} className="text-[9px] uppercase px-1 py-0">
                                {t.priority}
                              </Badge>
                            </div>
                          </div>
                          <h5 className="text-xs font-semibold text-brand-ink">{t.title}</h5>
                          <div className="flex items-center justify-between text-[11px] text-brand-muted pt-1 border-t border-brand-border/40">
                            <span>{t.assignee_name || 'Unassigned'}</span>
                            <span className="font-mono font-bold text-brand-ink">{t.progress_percent}%</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="pt-2 border-t border-brand-border flex justify-end">
                    <Button
                      size="sm"
                      onClick={() => setSelectedDayTasks(null)}
                      className="bg-brand text-brand-inverse hover:bg-brand-hover text-xs min-h-[36px]"
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
          </>
        )}

        {/* Task Detail Drawer */}
        {selectedTask && (
          <div className="fixed inset-y-0 right-0 w-full max-w-xl bg-white dark:bg-[#12211F] shadow-2xl z-50 flex flex-col border-l border-brand-border animate-in slide-in-from-right duration-200">
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-4 border-b border-brand-border bg-brand-surface dark:bg-[#0E1A18]">
              <div className="flex items-center space-x-2">
                <PlatformBadge platform={selectedTask.project_platform || 'direct'} size="xs" />
                <span className="text-xs font-semibold uppercase text-brand">
                  {selectedTask.project_name}
                </span>
                <Badge variant={selectedTask.status as any} className="capitalize text-xs">
                  {selectedTask.status.replace('_', ' ')}
                </Badge>
                {(() => {
                  const tm = selectedTask.milestone_id ? milestones.find((m) => m.id === selectedTask.milestone_id || String(m.id) === String(selectedTask.milestone_id)) : null;
                  return tm ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-teal/15 text-brand-teal border border-brand-teal/30 shrink-0" title={`Milestone: ${tm.title}`}>
                      <Flag className="w-2.5 h-2.5 shrink-0" />
                      <span className="truncate max-w-[140px]">{tm.title}</span>
                    </span>
                  ) : null;
                })()}
              </div>
              <button
                onClick={handleCloseDetail}
                className="text-brand-muted hover:text-brand-ink p-1.5 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close task details"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Drawer Body */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
              <div>
                <h2 className="text-lg font-bold text-brand-ink">{selectedTask.title}</h2>
                <p className="text-xs text-brand-muted mt-1 leading-relaxed">
                  {selectedTask.description || 'No task description provided.'}
                </p>
              </div>

              {/* Due Date & Project Metadata */}
              <div className="grid grid-cols-2 gap-3 p-3.5 rounded-xl bg-brand-surface dark:bg-[#152724] border border-brand-border text-xs">
                <div>
                  <span className="text-brand-muted uppercase tracking-wider text-[10px] font-semibold">Assignee</span>
                  <div className="font-semibold text-brand-ink mt-0.5">{selectedTask.assignee_name || 'Unassigned'}</div>
                </div>
                <div>
                  <span className="text-brand-muted uppercase tracking-wider text-[10px] font-semibold">Priority</span>
                  <div className="mt-0.5">
                    <Badge variant={selectedTask.priority as any} className="uppercase text-[9px]">
                      {selectedTask.priority}
                    </Badge>
                  </div>
                </div>
                <div className="col-span-2 pt-2 border-t border-brand-border">
                  <span className="text-brand-muted uppercase tracking-wider text-[10px] font-semibold block mb-1">
                    Project
                  </span>
                  {isOwner || isManager ? (
                    <select
                      value={selectedTask.project_id}
                      onChange={(e) => handleReassignProject(e.target.value)}
                      className="w-full text-base md:text-xs rounded-lg border border-brand-border p-2 bg-white dark:bg-[#12211F] text-brand-ink min-h-[44px]"
                    >
                      {projects.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="font-semibold text-brand-ink mt-0.5">{selectedTask.project_name}</div>
                  )}
                </div>
                <div className="col-span-2 pt-2 border-t border-brand-border">
                  <span className="text-brand-muted uppercase tracking-wider text-[10px] font-semibold block mb-1">
                    Due Date
                  </span>
                  <input
                    type="date"
                    disabled={isViewer}
                    value={selectedTask.due_date ? selectedTask.due_date.slice(0, 10) : ''}
                    onChange={(e) => handleUpdateDueDate(e.target.value)}
                    className="w-full text-base md:text-xs rounded-lg border border-brand-border p-2 bg-white dark:bg-[#12211F] text-brand-ink min-h-[44px]"
                  />
                </div>
                <div className="col-span-2 pt-2 border-t border-brand-border">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-brand-muted uppercase tracking-wider text-[10px] font-semibold block">
                      Milestone Deliverable
                    </span>
                    {selectedTask.milestone_id && !isViewer && canEditTask(selectedTask) && (
                      <button
                        type="button"
                        onClick={() => handleUpdateTaskMilestone(null)}
                        className="text-[10px] text-rose-500 hover:text-rose-700 underline font-medium"
                      >
                        Unlink Milestone
                      </button>
                    )}
                  </div>
                  {!isViewer && canEditTask(selectedTask) ? (
                    <select
                      value={selectedTask.milestone_id || ''}
                      onChange={(e) => handleUpdateTaskMilestone(e.target.value || null)}
                      className="w-full text-base md:text-xs rounded-lg border border-brand-border p-2 bg-white dark:bg-[#12211F] text-brand-ink min-h-[44px]"
                    >
                      <option value="">None (Independent Task)</option>
                      {milestones
                        .filter((m) => {
                          const pId = selectedTask.project_id;
                          const pSlug = selectedTask.project_slug;
                          const prj = projects.find((p) => p.id === pId || p.slug === pId);
                          return (
                            m.project_id === pId ||
                            (pSlug && m.project_id === pSlug) ||
                            (prj?.slug && m.project_id === prj.slug) ||
                            (prj?.id && m.project_id === prj.id) ||
                            String(m.project_id) === String(pId)
                          );
                        })
                        .map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.title}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <div className="font-semibold text-brand-ink mt-0.5 flex items-center space-x-1.5">
                      {selectedTask.milestone_id ? (
                        <>
                          <Flag className="w-3 h-3 text-brand-teal shrink-0" />
                          <span>{milestones.find((m) => m.id === selectedTask.milestone_id || String(m.id) === String(selectedTask.milestone_id))?.title || 'Linked Milestone'}</span>
                        </>
                      ) : (
                        <span className="text-brand-muted">None (Independent Task)</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Status Transitions */}
              {!isViewer && (
                <div className="space-y-2">
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-muted">
                    Workflow Status Transition
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {(isOwner || isManager
                      ? ['todo', 'in_progress', 'in_review', 'blocked', 'done']
                      : ['todo', 'in_progress', 'in_review', 'blocked']
                    ).map((st) => (
                      <Button
                        key={st}
                        size="sm"
                        variant={selectedTask.status === st ? 'default' : 'outline'}
                        onClick={() => handleUpdateStatus(st)}
                        className={`text-xs capitalize min-h-[38px] ${
                          selectedTask.status === st ? 'bg-brand text-white font-bold' : ''
                        }`}
                      >
                        {st.replace('_', ' ')}
                      </Button>
                    ))}
                  </div>
                  {selectedTask.blocked_reason && (
                    <div className="p-2.5 rounded-lg bg-rose-50 dark:bg-rose-950/30 text-xs text-rose-700 dark:text-rose-300 border border-rose-200 mt-2">
                      <strong>Blocked reason:</strong> {selectedTask.blocked_reason}
                    </div>
                  )}
                </div>
              )}

              {/* Blockers & AI Guidance Section */}
              <div className="space-y-3 pt-3 border-t border-brand-border">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-muted flex items-center space-x-1.5">
                    <ShieldAlert className="h-4 w-4 text-red-600" />
                    <span>Blockers & AI Guidance ({taskBlockers.length})</span>
                  </span>
                  {!isViewer && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setIsLoggingBlocker(!isLoggingBlocker)}
                      className="h-8 text-xs min-h-[36px] border-red-200 text-red-700 hover:bg-red-50"
                    >
                      <Plus className="h-3 w-3 mr-1 text-red-600" />
                      <span>{isLoggingBlocker ? 'Cancel' : 'Report Blocker'}</span>
                    </Button>
                  )}
                </div>

                {/* Inline Blocker Form */}
                {isLoggingBlocker && (
                  <div className="p-3.5 rounded-xl bg-red-50/50 border border-red-200 space-y-3 text-xs">
                    <div className="font-bold text-red-900 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-600" />
                      <span>Report Task Blocker with AI Guidance</span>
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-slate-700 mb-1">
                        1. What was the blocker? *
                      </label>
                      <textarea
                        rows={2}
                        placeholder="Explain the error, missing dependency, or roadblock..."
                        value={blockerForm.blocker_description}
                        onChange={(e) => setBlockerForm({ ...blockerForm, blocker_description: e.target.value })}
                        className="w-full text-xs p-2 rounded-md border border-red-200 bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-red-500"
                      />
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-[11px] font-semibold text-purple-900 flex items-center gap-1">
                          <Sparkles className="w-3 h-3 text-purple-600" />
                          2. What did AI say? (AI diagnosis / guidance)
                        </label>
                        <button
                          type="button"
                          onClick={handleAutoDiagnoseDrawer}
                          className="text-[10px] font-semibold text-purple-700 hover:text-purple-900 bg-purple-100 hover:bg-purple-200 px-1.5 py-0.5 rounded transition"
                        >
                          Auto-Diagnose
                        </button>
                      </div>
                      <textarea
                        rows={2}
                        placeholder="What prompt did you run? What advice or code fix did AI provide?..."
                        value={blockerForm.ai_diagnosis}
                        onChange={(e) => setBlockerForm({ ...blockerForm, ai_diagnosis: e.target.value })}
                        className="w-full text-xs p-2 rounded-md border border-purple-200 bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-emerald-900 mb-1 flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                        3. What after that? (Action taken / next steps)
                      </label>
                      <textarea
                        rows={2}
                        placeholder="What steps did you take next? Trial outcome or resolution?..."
                        value={blockerForm.resolution_notes}
                        onChange={(e) => setBlockerForm({ ...blockerForm, resolution_notes: e.target.value })}
                        className="w-full text-xs p-2 rounded-md border border-emerald-200 bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      />
                    </div>

                    <div className="flex items-center justify-between pt-1">
                      <select
                        value={blockerForm.severity}
                        onChange={(e) => setBlockerForm({ ...blockerForm, severity: e.target.value as any })}
                        className="text-xs border border-brand-border rounded px-2 py-1 bg-white font-medium"
                      >
                        <option value="critical">Critical Severity</option>
                        <option value="high">High Severity</option>
                        <option value="medium">Medium Severity</option>
                        <option value="low">Low Severity</option>
                      </select>

                      <Button
                        size="sm"
                        onClick={handleLogTaskBlocker}
                        className="bg-red-600 hover:bg-red-700 text-white font-semibold text-xs h-8"
                      >
                        Log Blocker
                      </Button>
                    </div>
                  </div>
                )}

                {/* Existing Blockers List */}
                {taskBlockers.length > 0 ? (
                  <div className="space-y-2.5">
                    {taskBlockers.map((b: any) => (
                      <div
                        key={b.id}
                        className={`p-3 rounded-lg border text-xs space-y-2 ${
                          b.status === 'resolved'
                            ? 'bg-emerald-50/50 border-emerald-200'
                            : b.status === 'investigating'
                            ? 'bg-blue-50/50 border-blue-200'
                            : 'bg-red-50/50 border-red-200'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 font-bold">
                            <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold ${
                              b.status === 'resolved'
                                ? 'bg-emerald-200 text-emerald-900'
                                : b.status === 'investigating'
                                ? 'bg-blue-200 text-blue-900'
                                : 'bg-red-200 text-red-900'
                            }`}>
                              {b.status}
                            </span>
                            <span className="text-[10px] text-brand-muted uppercase">({b.severity} priority)</span>
                          </div>
                          <span className="text-[11px] text-brand-muted">
                            by {b.user_name}
                          </span>
                        </div>

                        {/* Pillar 1 */}
                        <div>
                          <span className="font-bold text-red-900 block text-[11px]">1. Roadblock:</span>
                          <p className="text-slate-800 text-xs pl-2 border-l-2 border-red-300 mt-0.5">
                            {b.blocker_description}
                          </p>
                        </div>

                        {/* Pillar 2 */}
                        {b.ai_diagnosis && (
                          <div>
                            <span className="font-bold text-purple-900 block text-[11px] flex items-center gap-1">
                              <Sparkles className="w-3 h-3 text-purple-600" />
                              2. AI Guidance:
                            </span>
                            <p className="text-slate-800 text-xs pl-2 border-l-2 border-purple-300 mt-0.5">
                              {b.ai_diagnosis}
                            </p>
                          </div>
                        )}

                        {/* Pillar 3 */}
                        {b.resolution_notes && (
                          <div>
                            <span className="font-bold text-emerald-900 block text-[11px] flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                              3. Action Taken / Resolution:
                            </span>
                            <p className="text-slate-800 text-xs pl-2 border-l-2 border-emerald-300 mt-0.5">
                              {b.resolution_notes}
                            </p>
                          </div>
                        )}

                        {/* Resolve Action */}
                        {b.status !== 'resolved' && (
                          <div className="pt-2 border-t border-slate-200/80">
                            {resolvingBlockerId === b.id ? (
                              <div className="space-y-1.5 p-2 bg-emerald-50 rounded border border-emerald-200">
                                <label className="text-[11px] font-bold text-emerald-900 block">
                                  3. What after that? (Action taken to resolve):
                                </label>
                                <textarea
                                  rows={2}
                                  placeholder="Describe what was done after AI advice (e.g. fix applied, tests passed)..."
                                  value={drawerResolveNotes}
                                  onChange={(e) => setDrawerResolveNotes(e.target.value)}
                                  className="w-full text-xs p-1.5 rounded border border-emerald-300 bg-white text-slate-800"
                                />
                                <div className="flex items-center justify-end gap-1.5">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setResolvingBlockerId(null)}
                                    className="text-xs h-6 px-2"
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size="sm"
                                    onClick={() => handleResolveTaskBlocker(b.id)}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-6 px-2"
                                  >
                                    Confirm Resolved
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex justify-end">
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setResolvingBlockerId(b.id);
                                    setDrawerResolveNotes(b.resolution_notes || 'Applied fix per AI recommendations.');
                                  }}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-6 px-2.5 font-semibold"
                                >
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Resolve Blocker
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  !isLoggingBlocker && (
                    <p className="text-xs text-brand-muted italic py-1">
                      No blockers logged on this task.
                    </p>
                  )
                )}
              </div>

              {/* File Attachments Section (P2) */}
              <div className="space-y-3 pt-3 border-t border-brand-border">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-muted flex items-center space-x-1.5">
                    <Paperclip className="h-4 w-4 text-brand" />
                    <span>File Attachments (Max 10 MB)</span>
                  </span>
                  {!isViewer && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="h-8 text-xs min-h-[36px]"
                    >
                      {isUploading ? (
                        <Loader2 className="h-3 w-3 animate-spin mr-1" />
                      ) : (
                        <Upload className="h-3 w-3 mr-1 text-brand" />
                      )}
                      <span>Upload File</span>
                    </Button>
                  )}
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleUploadAttachment}
                  className="hidden"
                  accept="image/*,.pdf,.doc,.docx,.txt,.zip"
                />

                {attachmentError && (
                  <div className="p-2.5 rounded bg-rose-50 text-rose-700 text-xs border border-rose-200">
                    {attachmentError}
                  </div>
                )}

                <div className="space-y-1.5">
                  {attachments.length === 0 ? (
                    <p className="text-xs text-brand-muted italic py-2">No files attached to this deliverable yet.</p>
                  ) : (
                    attachments.map((att) => (
                      <div
                        key={att.id}
                        className="flex items-center justify-between p-2.5 rounded-lg border border-brand-border bg-brand-surface dark:bg-[#152724] text-xs"
                      >
                        <div className="flex items-center space-x-2.5 min-w-0 pr-2">
                          <FileText className="h-4 w-4 text-brand flex-shrink-0" />
                          <div className="min-w-0">
                            <a
                              href={att.url || '#'}
                              target="_blank"
                              rel="noreferrer"
                              className="font-semibold text-brand hover:underline truncate block"
                            >
                              {att.name}
                            </a>
                            <span className="text-[10px] text-brand-muted">
                              {att.size_formatted} • Uploaded by {att.uploaded_by_name || 'Team member'}
                            </span>
                          </div>
                        </div>
                        {(!isViewer && (isOwner || isManager || att.uploaded_by_id === user?.id)) && (
                          <button
                            onClick={() => handleDeleteAttachment(att.id)}
                            className="text-brand-muted hover:text-rose-600 p-1 min-h-[32px] min-w-[32px] flex items-center justify-center rounded"
                            title="Delete attachment"
                            aria-label="Delete attachment"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Checklist Section */}
              <div className="space-y-3 pt-3 border-t border-brand-border">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-brand-muted flex items-center space-x-1.5">
                    <CheckSquare className="h-4 w-4 text-brand" />
                    <span>Checklist Supporting Evidence</span>
                  </span>
                </div>
                <div className="space-y-2">
                  {selectedTask.checklist?.map((item: any) => (
                    <label
                      key={item.id}
                      className="flex items-center space-x-2 text-xs text-brand-ink hover:bg-brand-surface p-1.5 rounded cursor-pointer min-h-[38px]"
                    >
                      <input
                        type="checkbox"
                        disabled={isViewer || (!isOwner && !isManager && selectedTask.assignee_id !== user?.id)}
                        checked={item.is_completed}
                        onChange={() => handleToggleChecklist(item.id, item.is_completed)}
                        className="accent-brand-teal h-4 w-4 rounded"
                      />
                      <span className={item.is_completed ? 'line-through text-brand-muted' : ''}>
                        {item.text}
                      </span>
                    </label>
                  ))}
                </div>
                {!isViewer && (isOwner || isManager || selectedTask.assignee_id === user?.id) && (
                  <form onSubmit={handleAddChecklist} className="flex space-x-2">
                    <Input
                      value={newChecklistText}
                      onChange={(e) => setNewChecklistText(e.target.value)}
                      placeholder="Add item..."
                      className="h-10 text-base md:text-xs flex-1"
                    />
                    <Button type="submit" size="sm" variant="outline" className="min-h-[40px] text-xs">
                      Add
                    </Button>
                  </form>
                )}
              </div>

              {/* Comments Thread with @Mentions (P2) */}
              <div className="space-y-3 pt-3 border-t border-brand-border">
                <span className="text-xs font-bold uppercase tracking-wider text-brand-muted flex items-center space-x-1.5">
                  <MessageSquare className="h-4 w-4 text-brand" />
                  <span>Discussion &amp; @Mentions</span>
                </span>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedTask.comments?.map((c: any) => (
                    <div key={c.id} className="p-2.5 rounded-lg bg-brand-surface dark:bg-[#152724] text-xs space-y-1 border border-brand-border">
                      <div className="flex items-center justify-between font-semibold text-brand-ink">
                        <span>{c.user_name}</span>
                        <span className="text-[10px] text-brand-muted">
                          {new Date(c.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-brand-ink leading-relaxed whitespace-pre-wrap">{c.content}</p>
                    </div>
                  ))}
                </div>

                {!isViewer && (
                  <div className="relative">
                    {/* Autocomplete @Mentions Dropdown */}
                    {showMentions && (
                      <div className="absolute bottom-full left-0 mb-1 w-64 bg-white dark:bg-[#12211F] rounded-xl shadow-xl border border-brand-border z-20 overflow-hidden divide-y divide-brand-border">
                        <div className="px-3 py-1.5 text-[10px] font-bold text-brand-muted uppercase bg-brand-surface">
                          Mention Team Member
                        </div>
                        {teamMembers
                          .filter((m) => m.name?.toLowerCase().includes(mentionFilter))
                          .slice(0, 5)
                          .map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => handleSelectMention(m.name)}
                              className="w-full px-3 py-2 text-left text-xs text-brand-ink hover:bg-brand-tint flex items-center space-x-2"
                            >
                              <div className="h-5 w-5 rounded-full bg-brand-tint text-brand flex items-center justify-center font-bold text-[10px]">
                                {m.name?.charAt(0)}
                              </div>
                              <span className="font-semibold">{m.name}</span>
                            </button>
                          ))}
                      </div>
                    )}

                    <form onSubmit={handleAddComment} className="flex space-x-2">
                      <Input
                        value={newComment}
                        onChange={(e) => handleCommentChange(e.target.value)}
                        placeholder="Write a comment or type @ to mention..."
                        className="h-10 text-base md:text-xs flex-1"
                      />
                      <Button type="submit" size="sm" className="bg-brand text-white hover:bg-brand-hover min-h-[40px] text-xs font-semibold">
                        Send
                      </Button>
                    </form>
                  </div>
                )}
              </div>

              {/* Audit History */}
              <div className="space-y-2 pt-3 border-t border-brand-border">
                <span className="text-xs font-bold uppercase tracking-wider text-brand-muted flex items-center space-x-1.5">
                  <History className="h-4 w-4 text-brand" />
                  <span>Audit Timeline</span>
                </span>
                <div className="space-y-1.5 text-xs text-brand-muted">
                  {selectedTask.events?.map((ev: any) => (
                    <div key={ev.id} className="flex items-center justify-between py-1 border-b border-brand-border/40">
                      <span>
                        <strong className="text-brand-ink">{ev.actor_name}</strong>: {ev.event_type}
                        {ev.new_progress !== null && ` (${ev.new_progress}%)`}
                        {ev.reason && ` - ${ev.reason}`}
                      </span>
                      <span className="text-[10px] text-brand-muted font-mono">
                        {new Date(ev.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Drawer Footer (Owner ONLY Delete) */}
            <div className="p-4 border-t border-brand-border bg-brand-surface dark:bg-[#0E1A18] flex items-center justify-between">
              <span className="text-[11px] text-brand-muted font-mono">ID: {selectedTask.id}</span>
              {isOwner && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteTask(selectedTask.id, selectedTask.title)}
                  className="text-rose-600 border-rose-200 hover:bg-rose-50 dark:hover:bg-rose-950/30 text-xs flex items-center space-x-1 min-h-[38px]"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" />
                  <span>Delete Task</span>
                </Button>
              )}
            </div>
          </div>
        )}
        <DeleteConfirmModal
          isOpen={!!deleteTaskTarget}
          itemType="Task"
          itemName={deleteTaskTarget?.title}
          onClose={() => setDeleteTaskTarget(null)}
          onConfirm={confirmDeleteTask}
        />
      </div>
    </PullToRefresh>
  );
}

export default TasksPage;
